import { Link } from "react-router-dom";
import type { MyWorkDayEarnings, Shift, ShiftStatus } from "../../api/types";
import { formatTime24 } from "../../utils/time24";

interface Props {
  shifts: Shift[];
  /** G-123: own earnings per work day; null hides them (still loading or failed). */
  earnings: MyWorkDayEarnings[] | null;
  loading: boolean;
  error: string | null;
  /**
   * Where "Open full calendar" leads, or null to omit the link. Housekeeping
   * (G-103) has no `/app/my/shifts` — the shell bounces them back to Profile,
   * so the link was a dead end; S4 points it at their My-work page.
   */
  calendarPath?: string | null;
}

const STATUS_CLASS: Record<ShiftStatus, string> = {
  SCHEDULED: "badge muted",
  IN_PROGRESS: "badge primary",
  COMPLETED: "badge success",
  MISSED: "badge danger",
  CANCELED: "badge muted",
};

const fmtDateTime = (iso: string) => {
  try {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    return `${datePart}, ${formatTime24(iso)}`;
  } catch {
    return iso;
  }
};

const fmtTime = (iso: string) => {
  try {
    return formatTime24(iso);
  } catch {
    return iso;
  }
};

export function PersonalShiftsCard({
  shifts,
  earnings,
  loading,
  error,
  calendarPath = "/app/my/shifts",
}: Props) {
  const upcoming = shifts
    .filter((s) => s.status === "SCHEDULED" || s.status === "IN_PROGRESS")
    .slice(0, 5);
  const recent = shifts
    .filter((s) => s.status !== "SCHEDULED" && s.status !== "IN_PROGRESS")
    .slice(-5)
    .reverse();

  // A work day's total sits on its latest shift (OD-141: two shifts on one
  // day are one total); `recent` is newest first, so that is the first seen.
  const earningsByDay = new Map((earnings ?? []).map((e) => [e.workDate, e]));
  const totalOnShift = new Map<string, MyWorkDayEarnings>();
  const daysPlaced = new Set<string>();
  for (const s of recent) {
    const day = s.date.slice(0, 10);
    const e = earningsByDay.get(day);
    if (s.status === "COMPLETED" && e && !daysPlaced.has(day)) {
      daysPlaced.add(day);
      totalOnShift.set(s.id, e);
    }
  }

  return (
    <section
      className="card"
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: "var(--space-3)",
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>My shifts</h3>
          <p className="muted" style={{ margin: "var(--space-1) 0 0", fontSize: 13 }}>
            A snapshot of the last and next two weeks.
          </p>
        </div>
        {calendarPath ? (
          <Link className="button plain" to={calendarPath}>
            Open full calendar →
          </Link>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "var(--space-3)",
        }}
      >
        <div>
          <h4 style={{ margin: "0 0 var(--space-2)", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--color-text-muted)" }}>
            Upcoming
          </h4>
          {loading ? (
            <div className="muted">Loading…</div>
          ) : upcoming.length === 0 ? (
            <div className="empty-state" style={{ padding: "var(--space-3)" }}>
              No upcoming shifts.
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              {upcoming.map((s) => (
                <li
                  key={s.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "var(--space-3)",
                    padding: "var(--space-2) var(--space-3)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--color-surface-muted)",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{fmtDateTime(s.plannedStart)}</div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      until {fmtTime(s.plannedEnd)} · {s.location.name}
                    </div>
                  </div>
                  <span className={STATUS_CLASS[s.status]}>{s.status.replace("_", " ")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h4 style={{ margin: "0 0 var(--space-2)", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--color-text-muted)" }}>
            Recent
          </h4>
          {loading ? (
            <div className="muted">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="empty-state" style={{ padding: "var(--space-3)" }}>
              Nothing logged yet.
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              {recent.map((s) => (
                <li
                  key={s.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "var(--space-3)",
                    padding: "var(--space-2) var(--space-3)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--color-surface-muted)",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{fmtDateTime(s.plannedStart)}</div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {s.actualStart && s.actualEnd
                        ? `Worked ${fmtTime(s.actualStart)}–${fmtTime(s.actualEnd)}`
                        : `Planned until ${fmtTime(s.plannedEnd)}`}
                      {" · "}
                      {s.location.name}
                    </div>
                  </div>
                  <ShiftOutcome shift={s} earned={totalOnShift.get(s.id)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function ShiftOutcome({
  shift,
  earned,
}: {
  shift: Shift;
  earned: MyWorkDayEarnings | undefined;
}) {
  if (!earned) {
    return <span className={STATUS_CLASS[shift.status]}>{shift.status.replace("_", " ")}</span>;
  }
  const commission = Number(earned.commissionEur);
  // KI-169: a salaried month's shift earns no wage — the salary is monthly.
  if (earned.salaried) {
    return (
      <div className="shift-earned">
        <div className="shift-earned-total">
          {commission > 0 ? `€${commission.toFixed(2)}` : "—"}
        </div>
        <div className="muted shift-earned-note">
          {commission > 0 ? "commission · salaried" : "salaried"}
        </div>
      </div>
    );
  }
  if (earned.totalEur === null) {
    return (
      <div className="shift-earned">
        <div className="shift-earned-total">
          {commission > 0 ? `€${commission.toFixed(2)}` : "—"}
        </div>
        <div className="muted shift-earned-note">
          {commission > 0 ? "commission · no hourly rate" : "no hourly rate"}
        </div>
      </div>
    );
  }
  return (
    <div className="shift-earned">
      <div className="shift-earned-total">€{Number(earned.totalEur).toFixed(2)}</div>
      {commission > 0 ? (
        <div className="muted shift-earned-note">
          incl. €{commission.toFixed(2)} commission
        </div>
      ) : null}
    </div>
  );
}
